/**
 * i18n — lichte vertaallaag.
 *
 * Alle UI-tekst volgt de systeemtaal van het apparaat; valt die niet te
 * bepalen, dan is Engels de basis/fallback. (Kaart-/chakra-CONTENT staat in de
 * data en is nu Engels; per-taal contentgeneratie volgt later.)
 *
 * Geen externe library: een platte dictionary + `t()` met {var}-interpolatie.
 */
import type { Locale } from '../data/types';
import { FALLBACK_TAAL } from '../data/types';

export type Vertalingen = Record<string, string>;

const en: Vertalingen = {
  'app.subtitle': "The Chakra's Deck Building Game",

  'start.title': 'Begin a new journey',
  'start.world': 'World',
  'start.antagonist': 'Antagonist',
  'start.personage': 'Character',
  'start.name': 'Name',
  'start.namePlaceholder': 'Your name…',
  'start.random': 'Randomize',
  'start.begin': 'Begin run',
  'start.locked': 'Locked',

  'game.round': 'Round {n}',
  'game.energy': 'Energy',
  'game.knowledge': 'Knowledge',
  'game.endTurn': 'End turn',
  'game.market': 'Market',
  'game.hand': 'Hand',
  'game.playfield': 'Playfield',
  'game.deck': 'Deck',
  'game.discard': 'Discard',
  'game.codex': 'Codex',
  'game.menu': 'Menu',
  'game.buy': 'Buy',
  'game.antagonist': 'Antagonist',
  'game.emptyHand': 'No cards in hand — end your turn.',
  'game.emptyField': 'Play cards here',

  'level.leerling': 'Apprentice',
  'level.zoeker': 'Seeker',
  'level.gids': 'Guide',
  'level.meester': 'Master',

  'type.kern': 'Core',
  'type.eigenschap': 'Trait',
  'type.blokkade': 'Block',
  'type.herstel': 'Restore',
  'type.element': 'Element',
  'type.wijsheid': 'Wisdom',
  'type.combinatie': 'Combo',

  'card.cost': 'Cost',
  'card.gives': 'Gives',
  'card.locked': 'Keep playing to reveal more',
  'card.layer': 'Layer {n}',
  'card.body': 'Body',
  'card.history': 'History',
  'card.symbolism': 'Symbolism',
  'card.applications': 'Applications',
  'card.connections': 'Connections',
  'card.unseen': 'Undiscovered',

  'combo.activated': 'Combo: {name}',
  'blokkade.active': '{chakra} is blocked',
  'blokkade.hint': 'Play the matching restore card to clear it.',

  'quiz.title': 'Knowledge check',
  'quiz.correct': 'Correct!',
  'quiz.wrong': 'Not quite.',
  'quiz.next': 'Continue',

  'menu.title': 'Menu',
  'menu.resume': 'Resume',
  'menu.restart': 'New run',
  'menu.tutorial': 'Replay tutorial',
  'menu.volume': 'Volume',
  'menu.mute': 'Mute',
  'menu.unmute': 'Unmute',
  'menu.language': 'Language',
  'menu.close': 'Close',

  'onboarding.skip': 'Skip tutorial',
  'onboarding.next': 'Next',
  'onboarding.done': 'Start playing',
  'onboarding.step1.title': 'Your first chakra card',
  'onboarding.step1.body': 'Each card teaches a chakra. Hover to read it; long-press for the full entry.',
  'onboarding.step2.title': 'Play a card',
  'onboarding.step2.body': 'Drag a card to the playfield (or tap it). Playing costs energy and earns knowledge.',
  'onboarding.step3.title': 'Build a combo',
  'onboarding.step3.body': 'Activate two neighbouring chakras to trigger a combo bonus.',
  'onboarding.step4.title': 'The Codex',
  'onboarding.step4.body': 'Every card you play unlocks deeper layers in your Codex.',
  'onboarding.step5.title': 'Knowledge checks',
  'onboarding.step5.body': 'Answer questions to earn bonus knowledge and master each chakra.',

  'story.skip': 'Skip',
  'result.won': 'Balance restored',
  'result.lost': 'The wheels fall still',
  'result.playAgain': 'Play again',

  'codex.title': 'Codex',
  'codex.search': 'Search…',
  'codex.all': 'All',
  'codex.cardsDiscovered': '{found}/{total} cards',
  'codex.back': 'Back',
  'codex.noResults': 'Nothing found.',

  // Kaart-effecten
  'effect.kern': 'Activates this chakra',
  'effect.element': 'Elemental energy',
  'effect.eigenschap': 'Builds knowledge',
  'effect.blokkade': 'Blocks this chakra',
  'effect.herstel': 'Clears a block (+knowledge)',
  'effect.wijsheid': 'Deep insight (+knowledge)',
  'effect.combinatie': 'Combination effect',

  // Quizvragen + uitleg
  'quiz.q.kleur': 'Which color belongs to {chakra}?',
  'quiz.q.element': 'Which element belongs to {chakra}?',
  'quiz.q.sanskriet': 'What is the Sanskrit name of {chakra}?',
  'quiz.q.positie': 'Where in the body is {chakra} located?',
  'quiz.q.behoefte': 'What is the core need of {chakra}?',
  'quiz.q.frequentie': 'Which frequency belongs to {chakra}?',
  'quiz.e.kleur': '{chakra} is associated with the color {value}.',
  'quiz.e.element': 'The element of {chakra} is {value}.',
  'quiz.e.sanskriet': 'The Sanskrit name is {value}.',
  'quiz.e.positie': '{chakra}: {value}.',
  'quiz.e.behoefte': '{value}',
  'quiz.e.frequentie': '{chakra} resonates at {value}.',

  // Werelden
  'world.grid.sfeer': 'Black Mirror',
  'world.grid.desc': 'A world of screens, scores and silent control.',
  'world.empire.sfeer': 'Game of Thrones',
  'world.empire.desc': 'Thrones, intrigue and the weight of the crown.',
  'world.simulation.sfeer': 'Matrix',
  'world.simulation.desc': 'Nothing is real — except the will to awaken.',
  'world.colony.sfeer': 'Sci-fi',
  'world.colony.desc': 'Far from Earth, humanity seeks new roots.',
  'world.tribe.sfeer': 'Origin of humankind',
  'world.tribe.desc': 'Fire, stars and the first questions of consciousness.',
  'world.awakening.sfeer': 'Spiritual awakening',
  'world.awakening.desc': 'Here and now — the quiet revolution within.',
  'world.origin.sfeer': 'Mythic',
  'world.origin.desc': 'The source where the wheels first began to turn.',

  // Antagonisten
  'antag.conclave.tactiek': 'Economic control',
  'antag.conclave.blokkade': 'LifeScore cards',
  'antag.oracle.tactiek': 'Information manipulation',
  'antag.oracle.blokkade': 'Perception cards',
  'antag.shepherd.tactiek': 'Religious control',
  'antag.shepherd.blokkade': 'Faith blocks',
  'antag.machine.tactiek': 'Technological control',
  'antag.machine.blokkade': 'Frequency jamming',
  'antag.collective.tactiek': 'Peer pressure',
  'antag.collective.blokkade': 'Isolation cards',
  'antag.patriarch.tactiek': 'Hierarchical control',
  'antag.patriarch.blokkade': 'Power blocks',

  // Personages
  'pers.noa.kracht': 'Adaptability',
  'pers.mira.kracht': 'Fallen journalist — Throat chakra +2',
  'pers.kai.kracht': 'Hacker — starts with technical cards',
  'pers.elder.kracht': 'Old sage — historical cards are free',
  'pers.lena.kracht': 'Doctor — restore cards x2',
  'pers.ash.kracht': 'Former soldier — Root chakra +2',
  'pers.unknown.kracht': 'The unknown — form gains meaning through you',
};

const nl: Vertalingen = {
  'app.subtitle': 'Het Chakra Deck Building Spel',

  'start.title': 'Begin een nieuwe reis',
  'start.world': 'Wereld',
  'start.antagonist': 'Antagonist',
  'start.personage': 'Personage',
  'start.name': 'Naam',
  'start.namePlaceholder': 'Jouw naam…',
  'start.random': 'Willekeurig',
  'start.begin': 'Start run',
  'start.locked': 'Vergrendeld',

  'game.round': 'Ronde {n}',
  'game.energy': 'Energie',
  'game.knowledge': 'Kennis',
  'game.endTurn': 'Beurt beëindigen',
  'game.market': 'Markt',
  'game.hand': 'Hand',
  'game.playfield': 'Speelveld',
  'game.deck': 'Trekstapel',
  'game.discard': 'Aflegstapel',
  'game.codex': 'Codex',
  'game.menu': 'Menu',
  'game.buy': 'Koop',
  'game.antagonist': 'Antagonist',
  'game.emptyHand': 'Geen kaarten in hand — beëindig je beurt.',
  'game.emptyField': 'Speel hier kaarten',

  'level.leerling': 'Leerling',
  'level.zoeker': 'Zoeker',
  'level.gids': 'Gids',
  'level.meester': 'Meester',

  'type.kern': 'Kern',
  'type.eigenschap': 'Eigenschap',
  'type.blokkade': 'Blokkade',
  'type.herstel': 'Herstel',
  'type.element': 'Element',
  'type.wijsheid': 'Wijsheid',
  'type.combinatie': 'Combinatie',

  'card.cost': 'Kosten',
  'card.gives': 'Geeft',
  'card.locked': 'Speel vaker om meer te onthullen',
  'card.layer': 'Laag {n}',
  'card.body': 'Lichaam',
  'card.history': 'Geschiedenis',
  'card.symbolism': 'Symboliek',
  'card.applications': 'Toepassingen',
  'card.connections': 'Verbanden',
  'card.unseen': 'Onontdekt',

  'combo.activated': 'Combo: {name}',
  'blokkade.active': '{chakra} is geblokkeerd',
  'blokkade.hint': 'Speel de juiste herstelkaart om op te heffen.',

  'quiz.title': 'Kennisvraag',
  'quiz.correct': 'Juist!',
  'quiz.wrong': 'Niet helemaal.',
  'quiz.next': 'Verder',

  'menu.title': 'Menu',
  'menu.resume': 'Hervatten',
  'menu.restart': 'Nieuwe run',
  'menu.tutorial': 'Tutorial opnieuw',
  'menu.volume': 'Volume',
  'menu.mute': 'Dempen',
  'menu.unmute': 'Ontdempen',
  'menu.language': 'Taal',
  'menu.close': 'Sluiten',

  'onboarding.skip': 'Tutorial overslaan',
  'onboarding.next': 'Volgende',
  'onboarding.done': 'Begin met spelen',
  'onboarding.step1.title': 'Je eerste chakrakaart',
  'onboarding.step1.body': 'Elke kaart leert je een chakra. Houd erboven voor info; lang indrukken voor het volledige item.',
  'onboarding.step2.title': 'Speel een kaart',
  'onboarding.step2.body': 'Sleep een kaart naar het speelveld (of tik erop). Spelen kost energie en levert kennis op.',
  'onboarding.step3.title': 'Maak een combo',
  'onboarding.step3.body': 'Activeer twee naburige chakras om een combobonus te activeren.',
  'onboarding.step4.title': 'De Codex',
  'onboarding.step4.body': 'Elke gespeelde kaart ontsluit diepere lagen in je Codex.',
  'onboarding.step5.title': 'Kennisvragen',
  'onboarding.step5.body': 'Beantwoord vragen voor bonuskennis en meester elk chakra.',

  'story.skip': 'Overslaan',
  'result.won': 'Balans hersteld',
  'result.lost': 'De wielen vallen stil',
  'result.playAgain': 'Opnieuw spelen',

  'codex.title': 'Codex',
  'codex.search': 'Zoeken…',
  'codex.all': 'Alle',
  'codex.cardsDiscovered': '{found}/{total} kaarten',
  'codex.back': 'Terug',
  'codex.noResults': 'Niets gevonden.',

  // Kaart-effecten
  'effect.kern': 'Activeert dit chakra',
  'effect.element': 'Elementenergie',
  'effect.eigenschap': 'Bouwt kennis op',
  'effect.blokkade': 'Blokkeert dit chakra',
  'effect.herstel': 'Heft een blokkade op (+kennis)',
  'effect.wijsheid': 'Diep inzicht (+kennis)',
  'effect.combinatie': 'Combinatie-effect',

  // Quizvragen + uitleg
  'quiz.q.kleur': 'Welke kleur hoort bij {chakra}?',
  'quiz.q.element': 'Welk element hoort bij {chakra}?',
  'quiz.q.sanskriet': 'Wat is de sanskrietnaam van {chakra}?',
  'quiz.q.positie': 'Waar in het lichaam bevindt {chakra} zich?',
  'quiz.q.behoefte': 'Wat is de kernbehoefte van {chakra}?',
  'quiz.q.frequentie': 'Welke frequentie hoort bij {chakra}?',
  'quiz.e.kleur': '{chakra} wordt geassocieerd met de kleur {value}.',
  'quiz.e.element': 'Het element van {chakra} is {value}.',
  'quiz.e.sanskriet': 'De sanskrietnaam is {value}.',
  'quiz.e.positie': '{chakra}: {value}.',
  'quiz.e.behoefte': '{value}',
  'quiz.e.frequentie': '{chakra} resoneert op {value}.',

  // Werelden
  'world.grid.sfeer': 'Black Mirror',
  'world.grid.desc': 'Een wereld van schermen, scores en stille controle.',
  'world.empire.sfeer': 'Game of Thrones',
  'world.empire.desc': 'Tronen, intriges en de last van de kroon.',
  'world.simulation.sfeer': 'Matrix',
  'world.simulation.desc': 'Niets is echt — behalve de wil om te ontwaken.',
  'world.colony.sfeer': 'Sci-fi',
  'world.colony.desc': 'Ver van de aarde zoekt de mens naar nieuwe wortels.',
  'world.tribe.sfeer': 'Oorsprong van de mens',
  'world.tribe.desc': 'Vuur, sterren en de eerste vragen van het bewustzijn.',
  'world.awakening.sfeer': 'Spiritueel ontwaken',
  'world.awakening.desc': 'Hier en nu — de stille revolutie van binnenuit.',
  'world.origin.sfeer': 'Mythisch',
  'world.origin.desc': 'De bron waar de wielen voor het eerst begonnen te draaien.',

  // Antagonisten
  'antag.conclave.tactiek': 'Economische controle',
  'antag.conclave.blokkade': 'LifeScore kaarten',
  'antag.oracle.tactiek': 'Informatiemanipulatie',
  'antag.oracle.blokkade': 'Perceptiekaarten',
  'antag.shepherd.tactiek': 'Religieuze controle',
  'antag.shepherd.blokkade': 'Geloofsblokkades',
  'antag.machine.tactiek': 'Technologische controle',
  'antag.machine.blokkade': 'Frequentiestoring',
  'antag.collective.tactiek': 'Groepsdruk',
  'antag.collective.blokkade': 'Isolatiekaarten',
  'antag.patriarch.tactiek': 'Hiërarchische controle',
  'antag.patriarch.blokkade': 'Machtsblokkades',

  // Personages
  'pers.noa.kracht': 'Aanpassingsvermogen',
  'pers.mira.kracht': 'Gevallen journalist — Keelchakra +2',
  'pers.kai.kracht': 'Hacker — start met technische kaarten',
  'pers.elder.kracht': 'Oude wijze — historische kaarten gratis',
  'pers.lena.kracht': 'Dokter — herstelkaarten x2',
  'pers.ash.kracht': 'Voormalig soldaat — Wortelchakra +2',
  'pers.unknown.kracht': 'Het onbekende — vorm krijgt betekenis door jou',
};

export const WOORDENBOEK: Record<string, Vertalingen> = { en, nl };

/** Bepaalt de basistaalcode (zonder regio) uit de systeemtaal van het apparaat. */
export function detecteerTaal(): Locale {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : undefined;
    const taal = nav?.language ?? (nav?.languages && nav.languages[0]);
    if (taal) {
      const basis = taal.toLowerCase().split('-')[0];
      if (WOORDENBOEK[basis]) return basis;
    }
  } catch {
    /* val terug */
  }
  return FALLBACK_TAAL;
}

/** Vertaalt een sleutel; valt terug op Engels en daarna op de sleutel zelf. */
export function vertaal(sleutel: string, taal: Locale, vars?: Record<string, string | number>): string {
  const tabel = WOORDENBOEK[taal] ?? WOORDENBOEK[FALLBACK_TAAL];
  let tekst = tabel[sleutel] ?? WOORDENBOEK[FALLBACK_TAAL][sleutel] ?? sleutel;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) tekst = tekst.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
  }
  return tekst;
}
